import { useState } from 'react';
import {
  type ClaimCorrectionRecord,
  type ClaimCorrectionRuling,
  type ClaimCorrectionSourceType,
  recordClaimCorrection,
} from '../lib/api';
import { VoiceTextarea } from './VoiceTextarea';
import styles from './ClaimCorrectionControl.module.css';

/**
 * ClaimCorrectionControl — the "that's not right" affordance. Phase 2 Lane B
 * (D12 + the job-side half of D11: docs/design/fix-architecture-2026-07-08/
 * {FIX-ARCHITECTURE.md,BUILD-PLAN.md} Class 4), MIC IDIOM ADDED Phase C/C-P1
 * (docs/PLAN-MASTER-2026-07-09.md — "mic icon on the text input per rule 2").
 * Generalized from AlignmentReviewCard.tsx's Move/Keep/Flag-split/Merge
 * pattern (a small, per-claim button that writes ONE explicit human ruling)
 * down to the simplest shape this correction needs: tap -> a note (typed or
 * spoken) -> POST -> the claim renders corrected.
 *
 * USER-DRIVEN, NEVER AUTO-FIX: this NEVER rewrites `flaggedText` — the
 * caller keeps rendering the exact same claim text; this control only adds
 * the honest strikethrough/chip once `ruling` lands. Re-generation is a
 * SEPARATE, later act (the hub-write this POST triggers is what a future
 * regen reads) — tapping this button never itself changes any generated
 * artifact.
 *
 * Five states, per hard rule 5: idle (button), open (note input, mic-
 * capable per rule 2), saving (disabled), corrected (chip + undo), error
 * (inline retry). "Undo" writes a 'withdrawn' ruling — a NEW terminal row
 * (no deletions, ever; the disputed row stays queryable forever) that
 * returns the control to idle, for when a person flags something and then
 * changes their mind.
 */
export interface ClaimCorrectionControlProps {
  sourceType: ClaimCorrectionSourceType;
  sourceId: string;
  flaggedText: string;
  /** Present when the server already has a ruling for this exact claim
   *  (keyed by claimCorrectionKey at the caller) — renders the corrected
   *  state immediately, no round trip needed to know it's already handled. */
  existingCorrection?: ClaimCorrectionRecord | null;
  /** Optional: notify the caller once a correction lands, so a parent list
   *  can update its own local claimCorrectionKey map without a full refetch. */
  onCorrected?: (correction: ClaimCorrectionRecord) => void;
}

export function ClaimCorrectionControl({
  sourceType,
  sourceId,
  flaggedText,
  existingCorrection,
  onCorrected,
}: ClaimCorrectionControlProps) {
  const [phase, setPhase] = useState<'idle' | 'open' | 'saving' | 'error'>('idle');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [corrected, setCorrected] = useState<ClaimCorrectionRecord | null>(
    existingCorrection ?? null,
  );

  const submit = async (ruling: ClaimCorrectionRuling) => {
    setPhase('saving');
    setError(null);
    try {
      const { correction } = await recordClaimCorrection({
        sourceType,
        sourceId,
        flaggedText,
        userNote: note.trim() || undefined,
        ruling,
      });
      setCorrected(correction);
      setNote('');
      setPhase('idle');
      onCorrected?.(correction);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save that — try again.");
      setPhase('error');
    }
  };

  if (corrected && corrected.ruling === 'disputed') {
    return (
      <div className={styles.correctedRow}>
        <span className={styles.strikeText}>{flaggedText}</span>
        <div className={styles.correctedFooter}>
          <span className={styles.correctedChip}>
            Marked not right{corrected.userNote ? ` — ${corrected.userNote}` : ''}
          </span>
          <button
            type="button"
            className={styles.undoBtn}
            onClick={() => submit('withdrawn')}
            disabled={phase === 'saving'}
          >
            {phase === 'saving' ? 'Undoing…' : 'Undo'}
          </button>
        </div>
        {phase === 'error' && error ? <div className={styles.errorNote}>{error}</div> : null}
      </div>
    );
  }

  if (phase === 'open' || phase === 'saving' || phase === 'error') {
    return (
      <div className={styles.openColumn}>
        <VoiceTextarea
          label="What's actually true here? (optional)"
          value={note}
          onChange={setNote}
          rows={2}
          placeholder="Type it, or tap the mic and say it…"
          disabled={phase === 'saving'}
        />
        <div className={styles.openActions}>
          <button
            type="button"
            className={styles.confirmBtn}
            onClick={() => submit('disputed')}
            disabled={phase === 'saving'}
          >
            {phase === 'saving' ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={() => {
              setPhase('idle');
              setNote('');
              setError(null);
            }}
            disabled={phase === 'saving'}
          >
            Cancel
          </button>
        </div>
        {error ? <div className={styles.errorNote}>{error}</div> : null}
      </div>
    );
  }

  return (
    <button type="button" className={styles.flagBtn} onClick={() => setPhase('open')}>
      That's not right
    </button>
  );
}
